import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
    Zap, TrendingUp, TrendingDown, Activity, Cpu, RefreshCw, 
    ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const CHART_COLORS = {
    consumption: '#3B82F6',
    production: '#10B981',
    surplus: '#8B5CF6'
};

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds (to avoid too many API calls to Shelly)

export default function Dashboard() {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [summary, setSummary] = useState(null);
    const [chartType, setChartType] = useState('bar');
    const [chartMetric, setChartMetric] = useState('power'); // 'power', 'energy', or 'cost'
    const [devices, setDevices] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
    const intervalRef = useRef(null);

    // Fetch data from database (quick, no Shelly API call)
    const fetchData = useCallback(async () => {
        try {
            const [summaryRes, devicesRes] = await Promise.all([
                api.get('/dashboard/summary'),
                api.get('/devices')
            ]);
            setSummary(summaryRes.data);
            setDevices(devicesRes.data);
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    // Refresh data from Shelly Cloud (slower, but gets fresh readings)
    const refreshFromShelly = useCallback(async (showToast = true) => {
        try {
            setRefreshing(true);
            // This endpoint fetches fresh data from Shelly Cloud and returns updated summary
            const summaryRes = await api.post('/dashboard/refresh');
            setSummary(summaryRes.data);
            
            // Also refresh devices list
            const devicesRes = await api.get('/devices');
            setDevices(devicesRes.data);
            
            setLastUpdate(new Date());
            if (showToast) {
                toast.success('Dados atualizados da Shelly Cloud');
            }
        } catch (error) {
            console.error('Error refreshing from Shelly:', error);
            if (showToast) {
                toast.error('Erro ao atualizar dados');
            }
        } finally {
            setRefreshing(false);
        }
    }, [api]);

    const handleRefresh = () => {
        refreshFromShelly(true);
    };

    const generateDemoData = async () => {
        try {
            setRefreshing(true);
            await api.post('/demo/generate-data');
            await fetchData(true);
            toast.success('Dados de demonstração gerados com sucesso!');
        } catch (error) {
            console.error('Error generating demo data:', error);
            toast.error('Erro ao gerar dados de demonstração');
        } finally {
            setRefreshing(false);
        }
    };

    // Initial data fetch
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Auto-refresh polling - fetches fresh data from Shelly
    useEffect(() => {
        if (!autoRefreshEnabled) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        // Start polling - refresh from Shelly every 30 seconds
        intervalRef.current = setInterval(() => {
            console.log('Auto-refresh triggered - fetching from Shelly');
            refreshFromShelly(false);
        }, AUTO_REFRESH_INTERVAL);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [autoRefreshEnabled, refreshFromShelly]);

    const formatPower = (value) => {
        if (value >= 1000) {
            return `${(value / 1000).toFixed(2)} kW`;
        }
        return `${value?.toFixed(0) || 0} W`;
    };

    const getSurplusStatus = (surplus) => {
        if (surplus > 0) return { icon: ArrowUpRight, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Excedente' };
        if (surplus < 0) return { icon: ArrowDownRight, color: 'text-red-600', bg: 'bg-red-50', label: 'Défice' };
        return { icon: Minus, color: 'text-slate-600', bg: 'bg-slate-50', label: 'Equilibrado' };
    };

    const formatLastUpdate = () => {
        if (!lastUpdate) return '-';
        return lastUpdate.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    const renderChart = () => {
        const data = summary?.chart_data || [];
        
        if (data.length === 0) {
            return (
                <div className="h-[300px] flex items-center justify-center text-slate-500">
                    <div className="text-center">
                        <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>Sem dados para exibir</p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="mt-3"
                            onClick={generateDemoData}
                            data-testid="generate-demo-btn"
                        >
                            Gerar Dados Demo
                        </Button>
                    </div>
                </div>
            );
        }

        const commonProps = {
            data,
            margin: { top: 10, right: 30, left: 0, bottom: 0 }
        };

        // Determine which data keys and labels to use based on metric selection
        let consumptionKey, productionKey, consumptionLabel, productionLabel, unit;
        
        if (chartMetric === 'power') {
            consumptionKey = 'consumption_power';
            productionKey = 'production_power';
            consumptionLabel = 'Potência Máx. Consumo (kW)';
            productionLabel = 'Potência Máx. Produção (kW)';
            unit = 'kW';
        } else if (chartMetric === 'energy') {
            consumptionKey = 'consumption_energy';
            productionKey = 'production_energy';
            consumptionLabel = 'Energia Consumida (kWh)';
            productionLabel = 'Energia Produzida (kWh)';
            unit = 'kWh';
        } else { // cost
            consumptionKey = 'consumption_cost';
            productionKey = 'production_cost';
            consumptionLabel = 'Custo Consumo (€)';
            productionLabel = 'Receita Produção (€)';
            unit = '€';
        }

        const tooltipFormatter = (value, name) => {
            if (value === null || value === undefined) return ['N/A', name];
            return [`${value} ${unit}`, name];
        };

        switch (chartType) {
            case 'line':
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="hour" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `${v}`} />
                            <Tooltip 
                                formatter={tooltipFormatter}
                                contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
                            />
                            <Legend />
                            <Line type="monotone" dataKey={consumptionKey} stroke={CHART_COLORS.consumption} strokeWidth={2} name={consumptionLabel} dot={true} />
                            <Line type="monotone" dataKey={productionKey} stroke={CHART_COLORS.production} strokeWidth={2} name={productionLabel} dot={true} />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'bar':
            default:
                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart {...commonProps}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="hour" stroke="#64748B" fontSize={12} />
                            <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `${v}`} />
                            <Tooltip 
                                formatter={tooltipFormatter}
                                contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} 
                            />
                            <Legend />
                            <Bar dataKey={consumptionKey} fill={CHART_COLORS.consumption} name={consumptionLabel} radius={[4, 4, 0, 0]} />
                            <Bar dataKey={productionKey} fill={CHART_COLORS.production} name={productionLabel} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                );
        }
    };

    const renderDistributionChart = () => {
        const consumptionDevices = devices.filter(d => d.measurement_type === 'consumption');
        const data = consumptionDevices.map(d => ({
            name: d.name,
            value: d.last_reading?.power || 0
        })).filter(d => d.value > 0);

        if (data.length === 0) {
            return (
                <div className="h-[200px] flex items-center justify-center text-slate-500">
                    <p className="text-sm">Sem dados de consumo</p>
                </div>
            );
        }

        const COLORS = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444'];

        return (
            <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                    <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                    >
                        {data.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value) => formatPower(value)}
                        contentStyle={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                    />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        );
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="dashboard-loading">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-32 rounded-lg" />
                    ))}
                </div>
                <Skeleton className="h-96 rounded-lg" />
            </div>
        );
    }

    const surplusStatus = getSurplusStatus(summary?.surplus || 0);
    const SurplusIcon = surplusStatus.icon;

    return (
        <div className="space-y-6" data-testid="dashboard">
            {/* Status Bar */}
            <div className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-4 py-2">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <span className="relative flex h-2 w-2">
                            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${autoRefreshEnabled ? 'bg-emerald-400' : 'bg-slate-300'}`}></span>
                            <span className={`relative inline-flex rounded-full h-2 w-2 ${autoRefreshEnabled ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                        </span>
                        <button 
                            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                            className={`font-medium ${autoRefreshEnabled ? 'text-emerald-600' : 'text-slate-500'}`}
                        >
                            {autoRefreshEnabled ? 'Auto-refresh: 30s' : 'Auto-refresh: OFF'}
                        </button>
                    </div>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-500">
                        Atualizado: <strong>{formatLastUpdate()}</strong>
                    </span>
                </div>
                <Button
                    variant="default"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="refresh-dashboard-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'A atualizar...' : 'Atualizar Agora'}
                </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {/* Consumption Card */}
                <Card className="stat-card" data-testid="consumption-card">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Consumo Atual</p>
                                <p className="text-3xl font-bold text-slate-900 mt-1 font-heading">
                                    {formatPower(summary?.current_consumption || 0)}
                                </p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50">
                                <TrendingUp className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Production Card */}
                <Card className="stat-card" data-testid="production-card">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">Produção Atual</p>
                                <p className="text-3xl font-bold text-slate-900 mt-1 font-heading">
                                    {formatPower(summary?.current_production || 0)}
                                </p>
                            </div>
                            <div className="p-3 rounded-lg bg-emerald-50">
                                <Zap className="w-6 h-6 text-emerald-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Surplus Card */}
                <Card className="stat-card" data-testid="surplus-card">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-500">{surplusStatus.label}</p>
                                <p className="text-3xl font-bold text-slate-900 mt-1 font-heading">
                                    {formatPower(Math.abs(summary?.surplus || 0))}
                                </p>
                            </div>
                            <div className={`p-3 rounded-lg ${surplusStatus.bg}`}>
                                <SurplusIcon className={`w-6 h-6 ${surplusStatus.color}`} />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Devices Status Card */}
                <Card className="stat-card-gradient" data-testid="devices-status-card">
                    <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                            <div>
                                <p className="text-sm font-medium text-emerald-100">Dispositivos</p>
                                <p className="text-3xl font-bold mt-1 font-heading">
                                    {summary?.online_devices || 0}/{summary?.total_devices || 0}
                                </p>
                                <p className="text-xs text-emerald-200 mt-1">Online</p>
                            </div>
                            <div className="p-3 rounded-lg bg-white/20">
                                <Cpu className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Cost Summary Cards - Only shown if energy price is configured */}
            {summary?.energy_price && (
                <div className="grid gap-6 md:grid-cols-3">
                    {/* Today's Consumption Cost */}
                    <Card className="border-l-4 border-l-amber-500" data-testid="consumption-cost-card">
                        <CardContent className="pt-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Custo Consumo Hoje</p>
                                    <p className="text-3xl font-bold text-amber-600 mt-1 font-heading">
                                        {summary?.total_consumption_cost !== null 
                                            ? `${summary.total_consumption_cost.toFixed(2)} €` 
                                            : '-'
                                        }
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {summary?.total_consumption_energy?.toFixed(2) || 0} kWh
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-amber-50">
                                    <TrendingUp className="w-6 h-6 text-amber-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Cost by Period - Only for non-simple tariffs */}
                    {summary?.tariff_type && summary.tariff_type !== 'simples' && summary?.cost_by_period && (
                        <Card className="border-l-4 border-l-blue-500" data-testid="cost-by-period-card">
                            <CardContent className="pt-6">
                                <p className="text-sm font-medium text-slate-500 mb-3">Custo por Período</p>
                                <div className="space-y-2">
                                    {summary.cost_by_period.vazio !== undefined && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-600">Vazio</span>
                                            <div className="text-right">
                                                <span className="font-semibold text-blue-600">{summary.cost_by_period.vazio?.toFixed(2)} €</span>
                                                <span className="text-xs text-slate-400 ml-1">({summary.energy_by_period?.vazio?.toFixed(1)} kWh)</span>
                                            </div>
                                        </div>
                                    )}
                                    {summary.cost_by_period.cheias !== undefined && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-600">Cheias</span>
                                            <div className="text-right">
                                                <span className="font-semibold text-orange-600">{summary.cost_by_period.cheias?.toFixed(2)} €</span>
                                                <span className="text-xs text-slate-400 ml-1">({summary.energy_by_period?.cheias?.toFixed(1)} kWh)</span>
                                            </div>
                                        </div>
                                    )}
                                    {summary.cost_by_period.ponta !== undefined && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-slate-600">Ponta</span>
                                            <div className="text-right">
                                                <span className="font-semibold text-red-600">{summary.cost_by_period.ponta?.toFixed(2)} €</span>
                                                <span className="text-xs text-slate-400 ml-1">({summary.energy_by_period?.ponta?.toFixed(1)} kWh)</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Today's Production Value - only if no cost by period */}
                    {(!summary?.cost_by_period || summary.tariff_type === 'simples') && (
                        <Card className="border-l-4 border-l-emerald-500" data-testid="production-cost-card">
                            <CardContent className="pt-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-slate-500">Valor Produção Hoje</p>
                                        <p className="text-3xl font-bold text-emerald-600 mt-1 font-heading">
                                            {summary?.total_production_cost !== null 
                                                ? `${summary.total_production_cost.toFixed(2)} €` 
                                                : '-'
                                            }
                                        </p>
                                        <p className="text-xs text-slate-400 mt-1">
                                            {summary?.total_production_energy?.toFixed(2) || 0} kWh
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-emerald-50">
                                        <TrendingDown className="w-6 h-6 text-emerald-600" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Tariff Info */}
                    <Card className="border-l-4 border-l-slate-400" data-testid="tariff-info-card">
                        <CardContent className="pt-6">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm font-medium text-slate-500">Tarifário</p>
                                    <p className="text-lg font-bold text-slate-700 mt-1 font-heading">
                                        {summary?.tariff_type === 'simples' && 'Simples'}
                                        {summary?.tariff_type === 'bi_horario_diario' && 'Bi-horário Diário'}
                                        {summary?.tariff_type === 'bi_horario_semanal' && 'Bi-horário Semanal'}
                                        {summary?.tariff_type === 'tri_horario_diario' && 'Tri-horário Diário'}
                                        {summary?.tariff_type === 'tri_horario_semanal' && 'Tri-horário Semanal'}
                                    </p>
                                    <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                                        {summary?.tariff_prices?.simples && (
                                            <p>Preço: {summary.tariff_prices.simples.toFixed(4)} €/kWh</p>
                                        )}
                                        {summary?.tariff_prices?.vazio && (
                                            <p>Vazio: {summary.tariff_prices.vazio.toFixed(4)} €</p>
                                        )}
                                        {summary?.tariff_prices?.cheias && (
                                            <p>Cheias: {summary.tariff_prices.cheias.toFixed(4)} €</p>
                                        )}
                                        {summary?.tariff_prices?.ponta && (
                                            <p>Ponta: {summary.tariff_prices.ponta.toFixed(4)} €</p>
                                        )}
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg bg-slate-100">
                                    <Activity className="w-6 h-6 text-slate-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Main Chart */}
            <Card data-testid="main-chart">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                        <CardTitle className="font-heading">Consumo e Produção</CardTitle>
                        <CardDescription>Visão geral de hoje</CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Metric Toggle Buttons */}
                        <div className="flex items-center rounded-lg border border-slate-200 p-1" data-testid="chart-metric-toggle">
                            <button
                                onClick={() => setChartMetric('power')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    chartMetric === 'power' 
                                        ? 'bg-emerald-600 text-white' 
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                                data-testid="metric-power-btn"
                            >
                                Potência (kW)
                            </button>
                            <button
                                onClick={() => setChartMetric('energy')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                    chartMetric === 'energy' 
                                        ? 'bg-emerald-600 text-white' 
                                        : 'text-slate-600 hover:bg-slate-100'
                                }`}
                                data-testid="metric-energy-btn"
                            >
                                Energia (kWh)
                            </button>
                            {summary?.energy_price && (
                                <button
                                    onClick={() => setChartMetric('cost')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                        chartMetric === 'cost' 
                                            ? 'bg-amber-600 text-white' 
                                            : 'text-slate-600 hover:bg-slate-100'
                                    }`}
                                    data-testid="metric-cost-btn"
                                >
                                    Custo (€)
                                </button>
                            )}
                        </div>
                        <Select value={chartType} onValueChange={setChartType}>
                            <SelectTrigger className="w-32" data-testid="chart-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="area">Área</SelectItem>
                                <SelectItem value="line">Linha</SelectItem>
                                <SelectItem value="bar">Barras</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>
                <CardContent>
                    {renderChart()}
                </CardContent>
            </Card>

            {/* Bottom Grid */}
            <div className="grid gap-6 lg:grid-cols-3">
                {/* Distribution Chart */}
                <Card className="lg:col-span-1" data-testid="distribution-chart">
                    <CardHeader>
                        <CardTitle className="text-lg font-heading">Distribuição do Consumo</CardTitle>
                        <CardDescription>Por dispositivo</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {renderDistributionChart()}
                    </CardContent>
                </Card>

                {/* Recent Devices */}
                <Card className="lg:col-span-2" data-testid="recent-devices">
                    <CardHeader>
                        <CardTitle className="text-lg font-heading">Dispositivos</CardTitle>
                        <CardDescription>Estado atual dos dispositivos</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {devices.length === 0 ? (
                            <div className="text-center py-8 text-slate-500">
                                <Cpu className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p>Nenhum dispositivo configurado</p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-3"
                                    onClick={generateDemoData}
                                >
                                    Gerar Dados Demo
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {devices.slice(0, 5).map((device) => (
                                    <div
                                        key={device.id}
                                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                                        data-testid={`device-row-${device.id}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-2 h-2 rounded-full ${device.online ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                            <div>
                                                <p className="font-medium text-slate-900">{device.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    {device.device_type.toUpperCase()} • {device.measurement_type === 'consumption' ? 'Consumo' : 'Produção'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-semibold text-slate-900">
                                                {formatPower(device.last_reading?.power || 0)}
                                            </p>
                                            <p className="text-xs text-slate-400">
                                                {device.online ? 'Online' : 'Offline'}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
